import './styles.scss'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Grid, Row } from 'antd'
import type { FormInstance } from 'antd'
import clsx from 'clsx'

import { BillingAddress } from '@/components/DisplayAddress/BillingAddress'
import { DeliveryAddress } from '@/components/DisplayAddress/DeliveryAddress'
import { InvoiceIcon } from '@/components/Icons'
import { useCheckoutContext } from '@/contexts/checkout/useCheckoutContext'

import Article from '../Article'
import CostSummary from '../CostSummary'

interface UpperSectionProps {
  form: FormInstance
  submitFormHandler: (...args: unknown[]) => unknown
}

function UpperSection({ form, submitFormHandler }: UpperSectionProps) {
  const { t } = useTranslation()
  // Billing Address
  // Use billing address instead of customer address
  const { shouldShowServiceCode, billingInfo } = useCheckoutContext()
  const { md } = Grid.useBreakpoint()

  // Delivery Address
  const currentDeliveryAddr = form.getFieldValue('currentDeliveryAddr')
  const name = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrName')
    : form.getFieldValue('newAddrName')
  const name2 = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrName2')
    : form.getFieldValue('newAddrName2')
  const street = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrStreet')
    : form.getFieldValue('newAddrStreet')
  const street4 = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrStreet4')
    : form.getFieldValue('newAddrStreet4')
  const houseNumber = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrHouseNumber')
    : form.getFieldValue('newAddrHouseNumber')
  const zip = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrZip')
    : form.getFieldValue('newAddrZip')
  const city = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrCity')
    : form.getFieldValue('newAddrCity')
  const country = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrCountry')
    : form.getFieldValue('newAddrCountry')
  const region = currentDeliveryAddr === 1
    ? form.getFieldValue('myAddrRegion')
    : form.getFieldValue('newAddrRegion')

  // Service Code
  const serviceCode = form.getFieldValue('serviceCode')

  // Payment Method
  const paymentMethod = form.getFieldValue('paymentMethod')

  // Shipping
  const shippingMethod = form.getFieldValue('shippingMethod')

  // Reference Number
  const referenceNumber = form.getFieldValue('referenceNumber')

  const renderBillingAddress = () => (
    <Article
      classNames="fw-container"
      title={t('checkout.step3.upperSection.leftPart.billingAddress')}
    >
      <BillingAddress data={billingInfo} />
    </Article>
  )
  const renderDeliveryAddress = () => (
    <Article
      classNames="fw-container"
      title={t('checkout.step3.upperSection.leftPart.deliveryAddress')}
    >
      <DeliveryAddress
        data={{
          name,
          name2,
          street,
          houseNumber,
          street4,
          zip,
          city,
          country,
          region,
        }}
      />
    </Article>
  )

  const renderServiceCode = useMemo(() => {
    if (!shouldShowServiceCode || !serviceCode) {
      return null
    }

    return (
      <>
        <Row className={clsx(md && 'mb-6', !md && 'pl-5')}>
          <Col span={24}>
            <span>
              {t('checkout.serviceCode')}:{serviceCode}
            </span>
          </Col>
        </Row>
        <Divider className="divider--thin" />
      </>
    )
  }, [t, md, shouldShowServiceCode, serviceCode])

  const renderPaymentMethod = () => (
    <Article
      classNames="order-overview__upper-section__payment-method mt-5 fw-container"
      title={t('checkout.step3.upperSection.leftPart.paymentMethod')}
    >
      <InvoiceIcon style={{ marginRight: 10 }} /> {t(`checkout.step2.${paymentMethod}`)}
    </Article>
  )

  const renderShipping = () => (
    <Article
      classNames="order-overview__upper-section__payment-method mt-5 fw-container"
      title={t('checkout.step3.upperSection.leftPart.shipping')}
    >
      {shippingMethod || 'N/A'}
    </Article>
  )

  const renderReferenceNumber = () => (
    <Article
      classNames="order-overview__upper-section__reference-number mt-5 fw-container"
      title={t('checkout.step3.upperSection.leftPart.referenceNumber')}
    >
      {referenceNumber || 'N/A'}
    </Article>
  )

  const renderMobileView = () => (
    <section className="order-overview__upper-section">
      {renderDeliveryAddress()}
      <div className="order-overview__upper-section__divider fw-container mt-5 mb-4">
        <Divider className="divider--thin" />
      </div>
      {renderServiceCode}
      <div className="order-overview__upper-section__payment-title fw-container">
        {t('checkout.step3.upperSection.leftPart.mobile.paymentSection.title')}
      </div>
      {renderPaymentMethod()}
      {renderShipping()}
      {renderBillingAddress()}
      <div className="order-overview__upper-section__divider fw-container mt-4 mb-5">
        <Divider className="divider--thin" />
      </div>
      {renderReferenceNumber()}
      <CostSummary
        darkBackground
        showTitle
        showTotal
        form={form}
        submitFormHandler={submitFormHandler}
      />
    </section>
  )

  const renderDesktopView = () => (
    <section>
      <Row className="order-overview__upper-section">
        <Col
          className="order-overview__upper-section__left-part p-3"
          lg={16}
          md={16}
          sm={24}
          xl={16}
          xs={24}
        >
          <Row className="mb-6">
            <Col lg={12} md={12} sm={24} xl={12} xs={24}>
              {renderBillingAddress()}
            </Col>
            <Col lg={12} md={12} sm={24} xl={12} xs={24}>
              {renderDeliveryAddress()}
            </Col>
          </Row>
          {renderServiceCode}
          <Row className="mb-6">
            <Col lg={12} md={12} sm={24} xl={12} xs={24}>
              {renderPaymentMethod()}
            </Col>
            <Col lg={12} md={12} sm={24} xl={12} xs={24}>
              {renderReferenceNumber()}
            </Col>
          </Row>
          <Row>
            <Col lg={12} md={12} sm={24} xl={12} xs={24}>
              {renderShipping()}
            </Col>
            <Col lg={12} md={12} sm={24} xl={12} xs={24} />
          </Row>
        </Col>
        <Col
          className="order-overview__upper-section__right-part"
          lg={8}
          md={8}
          sm={24}
          xl={8}
          xs={24}
        >
          <CostSummary
            darkBackground
            showTitle
            showTotal
            form={form}
            submitFormHandler={submitFormHandler}
          />
        </Col>
      </Row>
    </section>
  )

  return !md ? renderMobileView() : renderDesktopView()
}

export default UpperSection
